import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Plus, Search, Loader2 } from 'lucide-react';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

/**
 * Tag input for commodities.
 *
 * Suggests commodity names already used elsewhere in the directory (with how
 * many companies use each) so naming stays uniform — that uniformity is what
 * makes "show me companies that sell X" work as a filter later. New values are
 * still allowed, but close existing matches are shown first to discourage
 * near-duplicates like "Soybean" vs "Soybeans".
 *
 * value: string[]   onChange: (string[]) => void
 */
const CommodityTagInput = ({
  value = [],
  onChange,
  testId = 'commodity-tags',
  options = null,        // fixed catalogue; when supplied no API lookup happens
  allowCreate = true,    // false = pick-only, nothing new can be typed in
  placeholder,
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  const selected = Array.isArray(value) ? value : [];
  const has = (name) => selected.some((v) => v.toLowerCase() === String(name).toLowerCase());

  // Fixed catalogue: filter in memory, no request.
  useEffect(() => {
    if (!options) return undefined;
    const term = query.trim().toLowerCase();
    const matched = (term ? options.filter((o) => o.toLowerCase().includes(term)) : options)
      .slice(0, 60)
      .map((name) => ({ name, count: null }));
    setSuggestions(matched);
    setLoading(false);
    return undefined;
  }, [query, options]);

  // Free-text mode: suggest what other companies already use.
  useEffect(() => {
    const term = query.trim();
    if (!open || options) return undefined;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/public/directory/commodities', {
          params: { q: term, limit: 12 },
        });
        setSuggestions(res.data.commodities || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [query, open, options]);

  // close when clicking away
  useEffect(() => {
    const onDown = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const add = useCallback((name) => {
    const clean = String(name || '').trim();
    if (!clean || has(clean)) { setQuery(''); return; }
    onChange([...selected, clean]);
    setQuery('');
    setActive(0);
  }, [selected, onChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = (name) => onChange(selected.filter((v) => v !== name));

  const unused = suggestions.filter((s) => !has(s.name));
  const exactExists = suggestions.some((s) => s.name.toLowerCase() === query.trim().toLowerCase());
  const canCreate = allowCreate && query.trim().length >= 2 && !exactExists && !has(query.trim());

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && unused[active]) add(unused[active].name);
      else if (allowCreate && query.trim()) add(query.trim());
    } else if (e.key === 'ArrowDown') {
      e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, Math.max(unused.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !query && selected.length) {
      remove(selected[selected.length - 1]);
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <div
        className="flex flex-wrap items-center gap-1.5 w-full px-2 py-1.5 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
        onClick={() => boxRef.current?.querySelector('input')?.focus()}
      >
        {selected.map((tag) => (
          <span
            key={tag}
            data-testid={`${testId}-chip`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
            style={{ background: `${BRAND_BLUE}12`, color: BRAND_BLUE }}
          >
            {tag}
            <button type="button" onClick={(e) => { e.stopPropagation(); remove(tag); }}
                    className="hover:text-red-600" aria-label={`Remove ${tag}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          data-testid={testId}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || (selected.length ? 'Add another...' : 'Start typing to search...')}
          className="flex-1 min-w-[150px] px-1 py-1 text-sm outline-none bg-transparent"
        />
      </div>

      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
          {loading && (
            <p className="px-3 py-2 text-xs text-gray-400 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Searching...
            </p>
          )}

          {!loading && unused.length === 0 && !canCreate && (
            <p className="px-3 py-2 text-xs text-gray-400">
              {options ? 'No match in the list — use the Other field below.' : 'No matches'}
            </p>
          )}

          {!loading && unused.length > 0 && (
            <>
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {options ? 'Choose from the list' : 'Already used in the directory'}
              </p>
              {unused.map((s, i) => (
                <button
                  key={s.name}
                  type="button"
                  data-testid={`${testId}-suggestion`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => add(s.name)}
                  className={`w-full flex items-center justify-between px-3 py-1.5 text-sm text-left ${
                    i === active ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <Search className="w-3 h-3 text-gray-300 flex-shrink-0" />
                    {s.name}
                  </span>
                  {s.count !== null && (
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {s.count} {s.count === 1 ? 'company' : 'companies'}
                    </span>
                  )}
                </button>
              ))}
            </>
          )}

          {!loading && canCreate && (
            <button
              type="button"
              data-testid={`${testId}-create`}
              onClick={() => add(query.trim())}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left border-t border-gray-100 text-gray-700 hover:bg-gray-50"
            >
              <Plus className="w-3.5 h-3.5" style={{ color: BRAND_BLUE }} />
              Add &ldquo;<strong>{query.trim()}</strong>&rdquo; as a new commodity
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CommodityTagInput;
